import {
  STORY_ACKNOWLEDGE_KINDS,
  STORY_ACKNOWLEDGE_NOTE_MIN_LENGTH,
  STORY_CHECK_ENVS,
  STORY_SPECS,
  type StoryAcknowledgeKind,
  type StoryCheckEnv,
  type StoryMaturity,
  type StorySpecKey,
} from '@safetyhub/shared';
import api from './api';

/**
 * 유저 스토리 검증 모니터 — admin API 클라이언트.
 *
 * 서버 정본: `apps/server/src/admin/admin-stories.controller.ts` ·
 * `admin-stories.service.ts`(응답 형상) · `dto/*.ts`(요청 형상).
 * 설계 정본: `docs/decisions/2026-10-07-prod-story-verification-monitor.md`.
 * 아래 타입은 그 서버 반환 형상을 **수동 동기화**한 것이다(관례: `lib/operations.ts`) —
 * 서버 쪽이 바뀌면 함께 고칠 것.
 *
 * ## 🔴 스토리 **본문**(`STORIES`)을 값으로 가져오지 않는다 — 그러나 금지선은 「shared」가 아니다
 * `packages/shared/src/stories/index.ts`가 그 사고를 기록하고 있다 — 그 **배럴 안의** top-level
 * 파생식 하나(`Object.keys(...)`)가 **275스토리 전문(불변식 SQL·결함 서술 포함)을 web 번들에 실어
 * prod로 나갔다**(`ErrorCode` 하나만 import한 엔트리가 502,415B → 그 선언을 지우니 14,757B).
 * 화면이 필요한 스토리 본문은 **전부 `story_definitions` 미러**(= 이 API)에서 오므로 `STORIES`를
 * 값으로 가져올 이유가 **영영 없다**.
 *
 * ⚠️ 그런데 그 금지선은 **모듈 단위**다 — 「shared에서 값을 가져오지 말라」가 아니라 **「top-level
 * 호출을 가진 모듈을 끌지 말라」**다(전개 `{ ...A }`는 rollup이 지우고, 멈추는 것은 호출이다).
 * 그래서 아래 둘은 **값으로 가져온다**. 둘 다 그 배럴 뒤의 형제 모듈이고 `import type`뿐·top-level
 * 호출 **0건**이라 같은 그래프 경로를 쓴다:
 * - `STORY_ACKNOWLEDGE_*` — 확인 처리 폼의 분류·최소 길이가 DTO 검증(`@IsIn`·`@MinLength`)과
 *   **같은 상수**여야 한다. 손나열하면 서버가 400을 내는 날까지 조용히 갈린다.
 * - `STORY_SPECS` — 명세 **이름**의 소유자다(2026-10-13). 종전엔 여기 `STORY_SPEC_LABELS`를 손으로
 *   베꼈고 **7개 중 2개가 이미 갈려** 같은 명세가 admin과 러너 보고에서 다른 이름으로 보였다.
 *   ⇒ 「번들 사고」를 근거로 라벨 표를 되살리지 말 것 — 그 사고의 원인은 이 모듈이 아니다.
 *
 * **재는 법**(merge 후 1회): `pnpm --filter admin build` 뒤 `grep -c 'US-0' apps/admin/dist/assets/*.js`가
 * **0**이어야 한다. ⚠️ **대조군을 함께** 세라 — `grep -c 'out-of-scope-cause\|파일 원장'`이 **≥ 1**이어야
 * 그 0이 「안 실렸다」이고, 아니면 그냥 **grep이 빗나간 것**이다(둘은 같은 모양으로 0을 낸다).
 */

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: null | { code: string; message: string };
}

/** `story_check_results.status` — 서버 enum(`StoryCheckStatus`)과 같은 집합. */
export type StoryCheckStatus = 'PASS' | 'VIOLATION' | 'VACUOUS' | 'ERROR';

/**
 * 무엇이 그 판정을 냈나 — 서버 enum(`StoryCheckSource`)과 같은 집합.
 *
 * ⚠️ **갈래는 둘이다.** 설계 문서는 `verifiedBy: 'auto' | 'claude' | 'human'` 세 갈래를 적었지만
 * DB·DTO에는 그 칸이 **없다**(`CreateStoryVerifyResultDto`에 `verifiedBy` 필드 0건). 남는 식별은
 * `recordedById`(슈퍼관리자) 하나이고, 스킬이 쓰든 사람이 손으로 쓰든 **같은 토큰**이라
 * 「Claude 판정」과 「사람 확인」을 가를 재료가 없다. 그래서 화면은 둘로만 가르고 그 사실을
 * 각주로 적는다 — 없는 신뢰도 구분을 그리면 **그 화면이 거짓이 된다**.
 */
export type StoryCheckSource = 'INVARIANT' | 'MANUAL';

/** 무엇이 그 실행을 불렀나. ⚠️ 지금 실제로 쓰이는 값은 `MANUAL`뿐이다(서버가 불변식을 돌리지 않는다). */
export type StoryCheckTrigger = 'MANUAL' | 'CRON' | 'DEPLOY';

/**
 * 측정 환경 — 접기 그레인 `(스토리, 불변식, env)`의 세 번째 축.
 *
 * ⚠️ **여기 손으로 적지 않는다** — 소유자는 shared `STORY_CHECK_ENVS`다(2026-10-13. 종전엔 이 줄에
 * `'dev' | 'prod'`가 따로 적혀 있어 같은 집합이 **세 자리**에 있었다: 여기 · shared
 * `StoryVerifyRecipe.target` · 서버 DTO. 값은 셋이 같았지만 넓히는 사람이 DTO만 보면 나머지가
 * 조용히 낡는 자리였다).
 * ⚠️ 이 모듈의 **타입만** 쓰는 소비처가 많아 `import type`으로 들어오지만, 드롭다운은 **런타임
 * 목록**(`STORY_CHECK_ENVS`)이 필요하다 — 그래서 아래에서 값도 함께 재export한다. 번들 판정은
 * 파일 헤더의 「금지선은 모듈 단위다」 절과 **같은 근거**다(그 모듈은 `import type`뿐·top-level
 * 호출 0건이라 같은 그래프 경로다).
 */
export type { StoryCheckEnv };

/**
 * 위반 집계 — **네 칸**이고 `open === openUnacknowledged + openAcknowledged`가 항등식이다.
 *
 * ⚠️ 이 네 칸의 합을 `total`이라 **부르지 않는다** — 같은 응답의 최상위 `total`은 **스토리 수**라
 * 한 이름이 두 가지를 뜻하게 된다(서버 쪽에서 실제로 그 상태가 됐고 그래서 고쳤다).
 * ⚠️ ack는 **면제가 아니다** — 큐(`openUnacknowledged`)에서만 빠지고 `open`에는 계속 센다.
 */
export interface ViolationTally {
  open: number;
  openUnacknowledged: number;
  openAcknowledged: number;
  resolved: number;
}

/** `story_definitions` 한 행 — 코드 레지스트리의 DB 미러. */
export interface StoryDefinition {
  /** `US-0013` 그 자체가 PK */
  id: string;
  specKey: string;
  /** 문서 번호(`P13`) — **식별자가 아니다.** 화면이 `US-0013 (사진 P13)`으로 둘 다 띄운다 */
  localNo: string;
  title: string;
  expectation: string;
  actor: string;
  triggers: string[];
  /** `verify` 절차의 종류(`query`/`code`/`browser`/`human`) — 없으면 null */
  verifyKind: string | null;
  /** ⚫ 작업 목록에서 빠진 사유 분류 — 「안 본다」가 아니라 「다른 데서 본다 / 볼 것이 없다」 */
  offQueueKind: string | null;
  offQueueBy: string | null;
  note: string | null;
  retiredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 판정이 어느 기준의 수인가 — 이 칸이 없으면 부분 run의 「위반 0」이 전수로 읽힌다. */
export interface StoryRunMeta {
  id: string;
  env: string;
  trigger: StoryCheckTrigger;
  measuredAt: string;
  baseSha: string | null;
  baseShaDirty: boolean | null;
  /** `--spec` 범위. **빈 배열 = 전수** */
  specFilter: string[];
}

/** 판정 열 한 칸 — 「무엇을 얼마로 쟀나」와 **대조군이 0이라 공허가 됐다**를 사람이 읽는 자리. */
export interface StoryCheckColumn {
  col: string;
  must: string;
  unit?: string | null;
  why?: string | null;
  value?: number | null;
  ok?: boolean | null;
}

/** (스토리, 불변식, env)별 **최신** 판정 한 줄. */
export interface StoryLatestResult {
  id: string;
  storyId: string;
  /** null = 사람이 내린 `MANUAL` 판정 */
  invariantName: string | null;
  source: StoryCheckSource;
  status: StoryCheckStatus;
  violationCount: number;
  /** `[{ col, must, unit, why, value, ok }]` — 서버가 `Json`으로 내려 형상이 보장되지 않는다 */
  columns: unknown;
  detail: string | null;
  checkedAt: string;
  run: StoryRunMeta;
}

/** 최근 결과 목록 한 줄 — `latestByInvariant`와 달리 `judgedAt`을 함께 든다. */
export interface StoryRecentResult {
  id: string;
  invariantName: string | null;
  source: StoryCheckSource;
  status: StoryCheckStatus;
  violationCount: number;
  detail: string | null;
  checkedAt: string;
  run: StoryRunMeta & { judgedAt: string };
}

/** 위반 **건** — 식별자·시각·ack 칸뿐이다(PII 없음 — 서버가 `select`를 그 목록으로 좁힌다). */
export interface StoryViolation {
  id: string;
  storyId: string;
  invariantName: string;
  entityType: string;
  entityId: string;
  /**
   * 어느 환경의 위반인가 — **유일 키의 다섯 번째 칸**이다
   * (`story_violations_entity_key = (storyId, invariantName, entityType, entityId, env)`).
   *
   * 🔑 그래서 **같은 entity가 dev·prod 2행으로 합법 존재한다** — 이 칸이 타입에 없으면 화면이 그 둘을
   * **중복으로** 보여주고 사람이 「같은 건이 두 번 떴다」로 읽는다. 상세 모달의 위반 행이 이 값을
   * 배지로 띄우는 이유다(「중복이 아니라 다른 env」가 그 자리에서 읽혀야 한다).
   * ⚠️ 그 축이 없던 동안 `dev` run의 빈 목록이 **prod가 찾아 둔 행까지** 해소 처리했다(스키마 주석).
   * ⚠️ 타입은 `StoryCheckEnv`가 아니라 **`string`**이다 — DB가 문자열 칼럼이고 러너가 `--env`로 임의
   * 값을 보낼 수 있어, 모르는 값이 와도 화면이 **그대로 보여주는** 쪽이 맞다(관례: `measuredEnvs`).
   */
  env: string;
  firstDetectedAt: string;
  lastDetectedAt: string;
  lastRunId: string | null;
  /** 다음 판정이 그 식별자를 **못 봤다**는 관측 — ack(사람의 주장)와 다른 축이다 */
  resolvedAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedById: string | null;
  acknowledgeKind: string | null;
  acknowledgeNote: string | null;
}

/** 목록 한 줄 — 정의 + 접힌 상태 + 위반 집계. */
export interface StoryListItem extends StoryDefinition {
  /** 접기 결과. **null = 판정 없음** — `PASS`와 섞지 않는다(아무것도 안 잰 것이 초록으로 보인다) */
  status: StoryCheckStatus | null;
  latestCheckedAt: string | null;
  /** 접기에 쓰인 (불변식, env) 칸 수 — 「무엇으로 접었나」의 분모 */
  resultCount: number;
  /** 어느 env에서 재졌나 — 이 칸이 없으면 dev 판정이 prod 판정처럼 읽힌다 */
  measuredEnvs: string[];
  /** 사람이 내린 판정이 섞여 있나 — 신뢰도가 달라 화면이 갈라 보여준다 */
  hasManualResult: boolean;
  violations: ViolationTally;
}

/**
 * 집계 — `byStatus`에 **`unjudged`가 따로** 있고, 건강도 분모는 `pass + violation`뿐이다.
 *
 * ⚠️ `vacuous`·`error`·`unjudged`를 **함께** 봐야 한다. 한 칸에 담으면 **아무도 안 쓰는 기능이
 * 100% 초록**으로 보인다(2026-10-11: `auto` 35건 중 12건이 공허였다).
 */
export interface StoryCounts {
  byStatus: {
    pass: number;
    violation: number;
    vacuous: number;
    error: number;
    /** 결과 0건 — 「아무도 안 쟀다」 */
    unjudged: number;
  };
  violations: ViolationTally;
  health: {
    pass: number;
    violation: number;
    denominator: number;
    /** 분모가 0이면 **null** — 0으로 나눈 100%를 만들지 않는다 */
    ratio: number | null;
    vacuous: number;
    error: number;
    unjudged: number;
  };
}

export interface StoryListResponse {
  stories: StoryListItem[];
  /** ⚠️ **스토리 수**다(필터 전체). 위반 수가 아니다 — 축이 다르다 */
  total: number;
  counts: StoryCounts;
}

export interface StoryDetailResponse {
  definition: StoryDefinition;
  status: StoryCheckStatus | null;
  latestByInvariant: StoryLatestResult[];
  recentResults: StoryRecentResult[];
  violations: StoryViolation[];
  violationTally: ViolationTally;
}

/**
 * 한 번의 판정 실행 — **「수에는 기준이 붙어야 한다」가 이 표의 존재 이유다**.
 *
 * ⚠️ `registrySummary`를 **`unknown`으로 둔다.** 형상 소유자는 **러너**(`scripts/stories/check.mjs`의
 * `summarize`)이고 **서버는 검증하지 않고 그대로 투과**한다(팀장 확정 계약 2026-10-13). 그래서 화면은
 * 「키 부재」에 견뎌야 하고, 그 판단을 한 자리에 모은 것이 `parseRegistrySummary`다 — `columns`와
 * 같은 규율이다(낯선 형상을 빈 값으로 접지 않는다).
 */
export interface StoryCheckRunSummary {
  id: string;
  env: string;
  trigger: StoryCheckTrigger;
  /** SQL을 **실제로 돌린** 시각 — `judgedAt`과 다르다 */
  measuredAt: string;
  judgedAt: string;
  baseSha: string | null;
  baseShaDirty: boolean | null;
  /** ⚠️ **빈 배열 = 전수.** 비어 있지 않으면 부분 run이고, 그 칸이 없으면 부분 run의 「위반 0」이 전수로 읽힌다 */
  specFilter: string[];
  tally: { pass: number; violation: number; vacuous: number; error: number };
  /** 러너 소유 형상 · **nullable** — `parseRegistrySummary`로만 읽는다 */
  registrySummary: unknown;
  recordedById: string | null;
}

export interface StoryCheckRunsResponse {
  items: StoryCheckRunSummary[];
  total: number;
}

export interface StoryCheckRunsParams {
  /** 1~50, 기본 10 */
  limit?: number;
  env?: string;
}

export interface StoryListParams {
  specKey?: string;
  status?: StoryCheckStatus;
  /** 생략하면 전 env 합산(그레인에 env가 있어 dev가 prod를 덮지 않는다) */
  env?: StoryCheckEnv;
  hasViolation?: boolean;
  includeRetired?: boolean;
  limit?: number;
  offset?: number;
}

export interface RecordVerifyResultBody {
  status: StoryCheckStatus;
  env: StoryCheckEnv;
  /** 실제로 재 본 시각(ISO) — 기록 시각(`judgedAt`)과 다르다 */
  measuredAt: string;
  /** 판정 근거 — 쓴 쿼리와 받은 숫자. ⚠️ PII 금지(상세 화면에 그대로 나간다) */
  detail?: string | null;
  baseSha?: string | null;
  baseShaDirty?: boolean | null;
}

export interface AcknowledgeViolationBody {
  kind: StoryAcknowledgeKind;
  /** 왜 이 건이 그 분류인가. ⚠️ PII 금지 */
  note: string;
}

/**
 * 명세 키 → 화면 이름. **소유자는 shared `STORY_SPECS[].title` 하나다**(2026-10-13).
 *
 * 종전엔 여기 `Record<StorySpecKey, string>` 라벨 표를 손으로 적었다. 키 누락은 과연 tsc가 막았지만
 * **값의 드리프트는 아무도 막지 못했고** 7개 중 2개가 이미 갈려 있었다(`file-registry`: 파일 원장 ↔
 * 파일 등록부 · `diff-review-gaps`) — 같은 명세가 admin 화면과 러너 보고에서 **다른 이름**으로 보였고
 * 둘 다 소비처가 있어 어느 쪽도 죽은 코드가 아니었다.
 * ⇒ 이름은 **한 벌**이다. 「화면에는 짧은 라벨이 필요하다」는 이 표를 되살릴 이유가 **아니다** —
 * 그때 고칠 곳은 `STORY_SPECS`의 그 `title`이고, 그 JSDoc이 소유 관계의 정본이다.
 * ⚠️ 파일 헤더의 번들 사고를 근거로 쓰지 말 것 — 그 사고의 원인은 **배럴 안의 top-level 호출**이고
 * `specs.ts`가 아니다(헤더의 「금지선은 모듈 단위다」 절).
 *
 * ⚠️ **모르는 키는 키를 그대로** 보여준다(라벨 누락이 화면에서 눈에 보이게 + DB 미러가 코드보다
 * 앞설 수 있다 — 은퇴한 명세). 그래서 조회는 반드시 이 함수로 하고 `STORY_SPECS[k].title`을 직접
 * 읽지 말 것 — 미러에만 있는 키에서 `undefined`를 읽는다.
 */
export function specLabel(specKey: string): string {
  return STORY_SPECS[specKey as StorySpecKey]?.title ?? specKey;
}

/**
 * 명세 키 전수 — 레지스트리 **선언 순서**(= `STORY_ID_BLOCKS` 블록 순서라 사람이 기대하는 순서다).
 *
 * ⚠️ **상수가 아니라 함수인 것이 의도다.** `Object.keys(...)`를 top-level에 두면 그 호출이 부작용으로
 * 보여 트리셰이킹이 멈추는데, 그것이 2026-10-11에 275스토리 전문을 prod 번들에 실은 **정확히 그
 * 메커니즘**이다. `shared/stories/index.ts`가 그 사고 기록 끝에 처방을 적어 뒀다 — 「런타임에 필요해지면
 * 상수가 아니라 **함수**로 두어라, 모듈 초기화 때 호출이 일어나지 않아야 한다」. 호출은 **소비처의
 * `useMemo` 안**에서 한다.
 *
 * 쓰는 자리는 명세 드롭다운이다. **응답에서 파생시키면 필터가 자기 선택지를 먹는다** — 명세 A를 고른
 * 순간 목록이 A 하나로 줄어 B로 바로 갈 수 없다(`specFilterOptions`가 그 합집합을 만든다).
 */
export function storySpecKeys(): StorySpecKey[] {
  return Object.keys(STORY_SPECS) as StorySpecKey[];
}

/**
 * 확인 처리 분류 라벨 — 키 집합은 shared 상수에서 **파생**된다(`Record<StoryAcknowledgeKind, …>`).
 * 분류를 늘리면 tsc가 여기 한 줄을 요구한다.
 */
export const STORY_ACKNOWLEDGE_KIND_LABELS: Record<
  StoryAcknowledgeKind,
  { label: string; hint: string }
> = {
  'out-of-scope-cause': {
    label: '원인이 이 스토리 밖',
    hint: '그 상태는 실재하지만 이 스토리가 말하는 행위가 만든 것이 아니다 (불변식이 스토리보다 넓다).',
  },
  fixed: {
    label: '고쳤다',
    hint: '코드를 수정했고 다음 판정에서 사라질 것으로 본다. ⚠️ 다시 보이면 큐로 돌아온다.',
  },
  'accepted-risk': {
    label: '알고 받아들인 위험',
    hint: '고치지 않기로 «결정»했다. 미루는 것이면 이 분류가 아니라 큐에 남기는 쪽이 정직하다.',
  },
  'spec-gap': {
    label: '명세가 틀렸다',
    hint: '위반이 아니라 스토리·불변식 쪽을 고쳐야 한다. 명세 개정이 선행 작업이다.',
  },
};

export { STORY_ACKNOWLEDGE_KINDS, STORY_ACKNOWLEDGE_NOTE_MIN_LENGTH, STORY_CHECK_ENVS };
/**
 * shared 접촉면은 **이 파일 하나**다 — 번들 규율(헤더)이 적힌 자리를 모든 import가 통과하게.
 * `StoryMaturity`는 러너가 내는 숙성 네 값(`auto`/`manual`/`none`/**`offQueue`**)이고
 * `storyMonitorView`의 분포 칸이 `Record<StoryMaturity, number>`로 받는다 — 값이 늘면 tsc가 그 칸을
 * 요구한다(생산자가 untyped `.mjs`라 **그 선언만이** 어긋남을 잡는 자리다).
 */
export type { StoryAcknowledgeKind, StoryMaturity };

/**
 * `manual` 판정이 낡았다고 볼 **경과일** 임계.
 *
 * 🔴 **이것은 「그 뒤 배포 횟수」가 아니다.** 설계가 요구한 신호는 「판정 시각 + 그 뒤 배포 N회」인데
 * 이 화면은 **그 횟수를 셀 수 없다** — admin이 아는 sha는 `__COMMIT_HASH__`(**admin 번들 자신**)이고
 * 서버 배포 sha·배포 목록을 내리는 엔드포인트가 없다(레포 전수 grep 0건). 그래서 **경과 시간**을
 * 대리 신호로 쓰고 화면에 그 한계를 적는다. 라벨을 「배포 뒤 미재판정」이라 **쓰지 않는다**(다른 측정이다).
 * 되짚을 조건: 서버가 배포 sha(또는 `GET /admin/stories/check-runs`)를 내리게 되면 그 축으로 바꾼다.
 */
export const MANUAL_RESULT_STALE_DAYS = 7;

/**
 * 「그 조회가 이 서버에 **아직 없다**」 — 404.
 *
 * 🔑 **에러가 아니라 사실이다.** 최근 실행 조회는 같은 라운드에 들어오는 엔드포인트라, 아직 없는
 * 서버(배포 전 prod·구 dev)에서 화면이 **깨지지 않고 degrade**해야 한다. 그래서 호출은 그대로 하고
 * 404만 갈라 다른 문구를 띄운다 — 엔드포인트가 들어오면 **그 자리가 한 줄도 안 바뀌고 켜진다.**
 *
 * ⚠️ 404를 다른 실패와 섞지 말 것. 403(권한 없음)·401(만료)·네트워크 실패는 **다른 사실**이고,
 * 한 문구로 뭉치면 「엔드포인트가 없다」가 「권한이 없다」를 덮어 원인이 숨는다.
 * ⚠️ 구조로 판정한다(`isAxiosError` + `status`) — axios 인스턴스를 또 import하지 않기 위해서다.
 */
export function isMissingEndpointError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as { isAxiosError?: unknown; response?: { status?: unknown } };
  return candidate.isAxiosError === true && candidate.response?.status === 404;
}

/** 경과일(내림). 입력이 null이면 null — 「0일」로 만들지 않는다(판정이 없는 것과 섞인다). */
export function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / 86_400_000);
}

/**
 * 「사람이 내린 판정에 기대고 있는데 그게 낡았다」 — ⚠️ 큐.
 *
 * 조건 둘을 **함께** 본다: 수동 판정이 섞여 있고(`hasManualResult`) 그 접힌 판정이 임계보다 오래됐다.
 * 불변식 판정만으로 초록인 스토리는 사람 손과 무관하게 다시 재지므로 이 큐가 아니다.
 */
export function isManualResultStale(story: StoryListItem): boolean {
  if (!story.hasManualResult) return false;
  const days = daysSince(story.latestCheckedAt);
  return days !== null && days >= MANUAL_RESULT_STALE_DAYS;
}

export const storiesApi = {
  async list(params?: StoryListParams): Promise<StoryListResponse> {
    const query = new URLSearchParams();
    if (params?.specKey) query.set('specKey', params.specKey);
    if (params?.status) query.set('status', params.status);
    if (params?.env) query.set('env', params.env);
    // ⚠️ 불리언은 `if (params?.x)`로 쓰지 않는다 — `false`를 보내야 하는 필터라 값 유무로 가른다
    if (params?.hasViolation !== undefined) {
      query.set('hasViolation', String(params.hasViolation));
    }
    if (params?.includeRetired !== undefined) {
      query.set('includeRetired', String(params.includeRetired));
    }
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query.set('offset', String(params.offset));

    const qs = query.toString();
    const res = await api.get<ApiResponse<StoryListResponse>>(
      `/admin/stories${qs ? `?${qs}` : ''}`,
    );
    return res.data.data;
  },

  /**
   * 최근 판정 실행 — 숙성 분포·커버리지 두 비율이 **여기서만** 온다(그 수는 `guard` 소스 스캔
   * 파생이라 서버가 모르고, run이 `registrySummary`에 기준과 함께 박은 것을 투과받는다).
   *
   * ⚠️ 엔드포인트가 아직 없는 서버에서는 **404**가 온다 — 그것은 에러가 아니라 「그 조회가 아직
   * 없다」는 사실이고, `isMissingEndpointError`로 갈라 화면이 다른 문구를 띄운다.
   */
  async listCheckRuns(params?: StoryCheckRunsParams): Promise<StoryCheckRunsResponse> {
    const query = new URLSearchParams();
    if (params?.limit !== undefined) query.set('limit', String(params.limit));
    if (params?.env) query.set('env', params.env);
    const qs = query.toString();
    const res = await api.get<ApiResponse<StoryCheckRunsResponse>>(
      `/admin/stories/check-runs${qs ? `?${qs}` : ''}`,
    );
    return res.data.data;
  },

  async get(storyId: string): Promise<StoryDetailResponse> {
    const res = await api.get<ApiResponse<StoryDetailResponse>>(
      `/admin/stories/${encodeURIComponent(storyId)}`,
    );
    return res.data.data;
  },

  /**
   * 사람이 내린 `manual` 판정 1건 기록.
   * ⚠️ 서버는 불변식을 **돌리지 않는다** — 이것은 「실행」이 아니라 사람이 이미 내린 판정의 **기록**이다.
   */
  async recordVerifyResult(
    storyId: string,
    body: RecordVerifyResultBody,
  ): Promise<{ runId: string; resultId: string; status: StoryCheckStatus; checkedAt: string }> {
    const res = await api.post<
      ApiResponse<{ runId: string; resultId: string; status: StoryCheckStatus; checkedAt: string }>
    >(`/admin/stories/${encodeURIComponent(storyId)}/verify-result`, body);
    return res.data.data;
  },

  /** 위반 건 확인 처리 — **큐에서만** 뺀다(위반 수에는 계속 센다). */
  async acknowledgeViolation(
    violationId: string,
    body: AcknowledgeViolationBody,
  ): Promise<StoryViolation> {
    const res = await api.patch<ApiResponse<StoryViolation>>(
      `/admin/stories/violations/${encodeURIComponent(violationId)}/acknowledge`,
      body,
    );
    return res.data.data;
  },
};
